import React, { useState } from 'react';
import { Sprout, Eye, EyeOff, Loader2 } from 'lucide-react';

// ── Shared HUD auth kit (web) ────────────────────────────────────────────────
// Deep forest / obsidian background, frosted glass card with soft lime inner
// glow, cream typography. Matches the app's HUD system. Used by Login, Create
// Account and Reset Password so the three screens are one cohesive product.

export const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', lime: '#B4D330',
  amber: '#E7B64B', terracotta: '#C66B4A', leaf: '#6B9F4A',
};

export function AuthShell({ children, subtitle, onLogoActivate, logoTestId }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'radial-gradient(circle at 50% -8%, #17432D 0%, #0F2D1F 46%, #081912 100%)' }}>
      <div className="w-full max-w-md relative" style={{ animation: 'hgAuthIn 520ms cubic-bezier(0.16,1,0.3,1)' }}>
        <style>{`@keyframes hgAuthIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
          @keyframes hgHalo{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:.7;transform:scale(1.12)}}`}</style>
        <div className="flex flex-col items-center mb-7">
          <div role="button" onClick={onLogoActivate} data-testid={logoTestId} title="Homegrown"
            className="relative flex items-center justify-center mb-4 select-none cursor-default" style={{ width: 76, height: 76 }}>
            <span className="absolute inset-0 rounded-full" aria-hidden="true"
              style={{ background: 'radial-gradient(circle, rgba(180,211,48,0.55), transparent 70%)', animation: 'hgHalo 2.4s ease-in-out infinite' }} />
            <span className="relative flex items-center justify-center rounded-full"
              style={{ width: 66, height: 66, background: C.forest, boxShadow: '0 10px 30px -6px rgba(0,0,0,0.6)' }}>
              <Sprout className="w-8 h-8" style={{ color: C.lime }} />
            </span>
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.cream, letterSpacing: '-0.02em' }}>Homegrown</h1>
          {subtitle ? <p className="mt-1.5 text-sm text-center" style={{ color: 'rgba(250,246,236,0.65)' }}>{subtitle}</p> : null}
        </div>
        <div className="rounded-[30px] p-8 sm:p-9"
          style={{ background: 'rgba(250,246,236,0.055)', backdropFilter: 'blur(26px)', WebkitBackdropFilter: 'blur(26px)',
            border: '1px solid rgba(180,211,48,0.16)', boxShadow: '0 30px 70px -24px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.08)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function HudLabel({ children }) {
  return <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(250,246,236,0.75)', letterSpacing: '0.08em' }}>{children}</label>;
}

export function HudInput({ icon: Icon, secure, value, onChange, dataTestid, ...rest }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      {Icon ? <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(250,246,236,0.5)' }} /> : null}
      <input
        data-testid={dataTestid}
        value={value}
        onChange={onChange}
        type={secure && !show ? 'password' : rest.type || 'text'}
        {...rest}
        className="w-full rounded-2xl py-3.5 text-[15px] focus:outline-none transition-all"
        style={{
          paddingLeft: Icon ? 42 : 16, paddingRight: secure ? 44 : 16,
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(250,246,236,0.14)', color: C.cream,
        }}
        onFocus={(e) => { e.target.style.border = `1px solid ${C.lime}`; e.target.style.boxShadow = '0 0 0 3px rgba(180,211,48,0.15)'; }}
        onBlur={(e) => { e.target.style.border = '1px solid rgba(250,246,236,0.14)'; e.target.style.boxShadow = 'none'; }}
      />
      {secure ? (
        <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)} data-testid={`${dataTestid}-toggle`}
          className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(250,246,236,0.55)' }}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      ) : null}
    </div>
  );
}

export function PrimaryButton({ children, dataTestid, ...rest }) {
  return (
    <button data-testid={dataTestid} {...rest}
      className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-bold text-[15px] transition-all active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100"
      style={{ background: C.lime, color: C.forestDeep, boxShadow: '0 10px 28px -8px rgba(180,211,48,0.7)' }}>
      {children}
    </button>
  );
}

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.9 36.2 44 30.6 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);
const AppleMark = () => (
  <svg width="17" height="17" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor">
    <path d="M318.7 268c-.3-37 16.4-64.9 50.3-85.7-19-27.2-47.5-42.2-85.1-45.1-35.6-2.8-74.6 20.9-88.8 20.9-15.1 0-49.6-19.9-76.7-19.9C72.1 139 16 182.6 16 270.6c0 26 4.8 52.9 14.3 80.6 12.7 36.5 58.6 126 106.5 124.5 25-.6 42.7-17.8 75.3-17.8 31.6 0 48 17.8 76.7 17.8 48.3-.7 89.8-82.1 101.9-118.7-64.8-30.5-61.9-89.4-61.9-91zM259.3 94.4c28.7-34 26.1-65 25.3-76-25.4 1.5-54.8 17.3-71.6 36.8-18.5 20.9-29.4 46.8-27 74.9 27.5 2.1 52.6-12 73.3-35.7z" />
  </svg>
);

export function SocialButton({ provider, onClick, dataTestid, label, loading, disabled }) {
  const google = provider === 'google';
  const [ripples, setRipples] = useState([]);
  const addRipple = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const id = Date.now() + Math.random();
    setRipples((r) => [...r, { id, x, y, size }]);
    setTimeout(() => setRipples((r) => r.filter((v) => v.id !== id)), 620);
  };
  const off = loading || disabled;
  return (
    <button type="button" onClick={onClick} data-testid={dataTestid} disabled={off}
      onPointerDown={(e) => { if (!off) addRipple(e); }}
      className="relative overflow-hidden w-full inline-flex items-center justify-center gap-2.5 rounded-full py-3 font-semibold text-[14px] transition-all active:scale-[0.97] disabled:active:scale-100"
      style={{
        opacity: off ? 0.75 : 1,
        ...(google
          ? { background: C.cream, color: '#2D241E', border: '1px solid rgba(0,0,0,0.06)' }
          : { background: '#000', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }),
      }}>
      <style>{`@keyframes hgRipple{to{transform:scale(2.6);opacity:0}}`}</style>
      {ripples.map((r) => (
        <span key={r.id} aria-hidden="true" style={{
          position: 'absolute', left: r.x, top: r.y, width: r.size, height: r.size, borderRadius: '9999px',
          background: google ? 'rgba(26,67,49,0.14)' : 'rgba(255,255,255,0.3)',
          transform: 'scale(0)', opacity: 0.6, animation: 'hgRipple 600ms ease-out forwards', pointerEvents: 'none' }} />
      ))}
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (google ? <GoogleMark /> : <AppleMark />)}
      {loading ? 'Connecting…' : label}
    </button>
  );
}

export function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: 'rgba(250,246,236,0.2)' };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  s = Math.min(4, s);
  const map = [
    { label: 'Too short', color: C.terracotta },
    { label: 'Weak', color: C.terracotta },
    { label: 'Fair', color: C.amber },
    { label: 'Good', color: C.lime },
    { label: 'Strong', color: C.leaf },
  ];
  return { score: s, ...map[s] };
}

export function AuthNotice({ kind = 'error', children, dataTestid }) {
  const err = kind === 'error';
  return (
    <div data-testid={dataTestid} className="flex items-start gap-2 text-sm rounded-2xl px-4 py-3"
      style={{ background: err ? 'rgba(198,107,74,0.14)' : 'rgba(180,211,48,0.14)', color: err ? '#F0B6A6' : C.lime }}>
      <span>{children}</span>
    </div>
  );
}
